import { Head, Link } from "@inertiajs/react"
import { Header } from "@/components/header"

export default function Terms() {
  return (
    <div className="min-h-screen">
      <Head title="Terms of Service" />
      <Header />

      <main className="container mx-auto px-4 py-8 max-w-3xl">
        <h1 className="text-2xl font-mono font-bold mb-8">TERMS OF SERVICE</h1>

        <div className="prose prose-invert prose-sm max-w-none space-y-8">
          <p className="text-muted-foreground">
            Last updated: January 2026
          </p>

          <section>
            <h2 className="text-lg font-mono font-bold mb-3">1. Acceptance of Terms</h2>
            <p className="text-muted-foreground leading-relaxed">
              By accessing or using Broken Sounds ("the Service"), you agree to be bound by these
              Terms of Service. If you do not agree to these terms, do not use the Service.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-mono font-bold mb-3">2. Description of Service</h2>
            <p className="text-muted-foreground leading-relaxed">
              Broken Sounds is a platform for sharing audio samples. Users can upload, browse,
              and download audio samples shared by the community.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-mono font-bold mb-3">3. User Accounts</h2>
            <p className="text-muted-foreground leading-relaxed">
              To upload samples, you must authenticate via a supported OAuth provider (Discord or GitHub).
              You are responsible for maintaining the security of your account and for all activities
              that occur under your account.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-mono font-bold mb-3">4. User Content</h2>
            <div className="text-muted-foreground leading-relaxed space-y-3">
              <p>
                By uploading content to Broken Sounds, you represent and warrant that:
              </p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>You own or have the necessary rights to the content</li>
                <li>Your content does not infringe on any third party's intellectual property rights</li>
                <li>Your content does not violate any applicable laws</li>
              </ul>
              <p>
                You retain ownership of content you upload. By uploading, you grant Broken Sounds a
                non-exclusive, worldwide license to host, display, and distribute your content through
                the Service.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-lg font-mono font-bold mb-3">5. Sample Licensing</h2>
            <div className="text-muted-foreground leading-relaxed space-y-3">
              <p>
                All samples shared on Broken Sounds are royalty-free. You may use downloaded samples
                in your music productions, including commercial releases, without attribution or payment
                to the original uploader.
              </p>
              <p>
                By uploading a sample, you grant all users a perpetual, worldwide, royalty-free license
                to use, modify, and incorporate your sample into their own works.
              </p>
              <p>
                You may not redistribute samples as standalone files, sample packs, or sound libraries.
                Samples must be used as part of a larger musical work.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-lg font-mono font-bold mb-3">6. Prohibited Content</h2>
            <div className="text-muted-foreground leading-relaxed space-y-3">
              <p>You may not upload content that:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Infringes on copyrights, trademarks, or other intellectual property rights</li>
                <li>Contains malware or harmful code</li>
                <li>Is illegal, defamatory, or obscene</li>
                <li>Impersonates any person or entity</li>
                <li>Violates any applicable law or regulation</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-lg font-mono font-bold mb-3">7. Copyright & DMCA</h2>
            <p className="text-muted-foreground leading-relaxed">
              We respect intellectual property rights. If you believe your copyrighted work has been
              uploaded without authorization, please see our{" "}
              <Link href="/dmca" className="text-primary hover:underline">
                DMCA Policy
              </Link>{" "}
              for information on how to submit a takedown request.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-mono font-bold mb-3">8. Termination</h2>
            <p className="text-muted-foreground leading-relaxed">
              We reserve the right to suspend or terminate your access to the Service at any time,
              for any reason, including violation of these Terms. Upon termination, your right to
              use the Service will immediately cease.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-mono font-bold mb-3">9. Disclaimer of Warranties</h2>
            <p className="text-muted-foreground leading-relaxed">
              The Service is provided "as is" without warranties of any kind, either express or
              implied. We do not guarantee that the Service will be uninterrupted, secure, or
              error-free.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-mono font-bold mb-3">10. Limitation of Liability</h2>
            <p className="text-muted-foreground leading-relaxed">
              To the maximum extent permitted by law, Broken Sounds shall not be liable for any
              indirect, incidental, special, consequential, or punitive damages arising from your
              use of the Service.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-mono font-bold mb-3">11. Changes to Terms</h2>
            <p className="text-muted-foreground leading-relaxed">
              We may modify these Terms at any time. Continued use of the Service after changes
              constitutes acceptance of the new Terms. We encourage you to review these Terms
              periodically.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-mono font-bold mb-3">12. Contact</h2>
            <p className="text-muted-foreground leading-relaxed">
              For questions about these Terms, please contact us at{" "}
              <a href="mailto:admin@brokensounds.net" className="text-primary hover:underline">
                admin@brokensounds.net
              </a>
            </p>
          </section>

          <div className="pt-4 border-t border-border">
            <p className="text-muted-foreground">
              See also our{" "}
              <Link href="/privacy" className="text-primary hover:underline">
                Privacy Policy
              </Link>
              {" and "}
              <Link href="/dmca" className="text-primary hover:underline">
                DMCA Policy
              </Link>
            </p>
          </div>
        </div>
      </main>
    </div>
  )
}
